/**
 * /academy/course — the path from A to Z (D-105, D-107: TCF Canada and IELTS), drawn like a
 * trail: levels, their units, and lessons locked, open or completed. The next lesson glows; XP,
 * the lessons completed and the review of the day sit on top; « Méthode et astuces » stands apart,
 * always open. The same screen serves both exams: only the texts and the content language change.
 */

import { ApiError, useAcademyCourseRetrieve, useAcademyFormatRetrieve } from "@agencyos/api-client";
import { type CoursePath, type PathLesson, type PathLevel, type PathUnit, findLesson, levelTarget, parseCoursePath, parseIeltsFormat, parseMethod, pick } from "@agencyos/core";
import { Badge, EmptyState, GlassCard, Skeleton, buttonClass } from "@agencyos/ui-web";
import { Check, ChevronDown, Flag, Lightbulb, Lock, Play, Repeat, Route, Sparkles, Star } from "lucide-react";
import { type CSSProperties, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { PageHead, Ring } from "../ui";
import { ContentLangProvider } from "./contentLang";
import { useCourseExam, useCourseText } from "./courseText";
import { IELTS as IELTS_DEFAULT, TCF, courseExam } from "./exam";
import { MotherTonguePrompt } from "./MotherTongue";

export function useCoursePath(enabled = true, exam: string = IELTS_DEFAULT) {
  const params = { exam: courseExam(exam) } as const;
  const query = useAcademyCourseRetrieve(params, { query: { retry: false, enabled, staleTime: 30_000 } });
  const raw = query.data?.status === 200 ? query.data.data : null;
  const path = useMemo(() => (raw ? parseCoursePath(raw) : null), [raw]);
  const method = useMemo(() => (raw ? parseMethod(raw.method) : null), [raw]);
  const locked = query.error instanceof ApiError && query.error.status === 403;
  return { query, path, method, locked };
}

export function useIeltsFormat(exam: string = IELTS_DEFAULT) {
  const params = courseExam(exam) === TCF ? {} : { exam: courseExam(exam) };
  const query = useAcademyFormatRetrieve(params, { query: { staleTime: 3_600_000, retry: false } });
  return useMemo(() => parseIeltsFormat(query.data?.status === 200 ? query.data.data : undefined), [query.data]);
}

/** The title in the content language, with the interface language below when it differs. */
function titles(value: unknown, content: string, language: string): { main: string; sub: string } {
  const main = pick(value, content);
  const sub = pick(value, language);
  return { main, sub: sub && sub !== main ? sub : "" };
}

function LessonNode({ lesson, next, index, exam }: { lesson: PathLesson; next: boolean; index: number; exam: string }) {
  const { i18n } = useTranslation();
  const { ct, contentLang } = useCourseText(exam);
  const title = pick(lesson.title, contentLang);
  const Icon = lesson.state === "completed" ? Check : lesson.state === "locked" ? Lock : next ? Play : Star;
  const state = next ? "next" : lesson.state;
  const body = (
    <>
      <span className="ac-node__disc" aria-hidden="true">
        <Icon size={next ? 24 : 20} strokeWidth={2.2} />
      </span>
      <span className="ac-node__text">
        <span className="ac-node__code ao-num">{lesson.code}</span>
        <span className="ac-node__title" lang={contentLang}>
          {title}
        </span>
        {!i18n.language.startsWith(contentLang) && titles(lesson.title, contentLang, i18n.language).sub && (
          <span className="ac-footnote">{titles(lesson.title, contentLang, i18n.language).sub}</span>
        )}
        <span className="visually-hidden">{ct(`course.state.${state}`)}</span>
        {lesson.state === "completed" && lesson.score !== null && (
          <span className="ac-node__score ao-num">{ct("course.score", { percent: Math.round(lesson.score * 100) })}</span>
        )}
        {next && <span className="ac-node__cta">{ct("course.start")}</span>}
      </span>
    </>
  );
  const style = { "--ac-node-i": index % 4 } as CSSProperties;
  return (
    <li className={`ac-node ac-node--${state}`} style={style}>
      {lesson.state === "locked" ? (
        <span className="ac-node__inner" aria-disabled="true">
          {body}
        </span>
      ) : (
        <Link className="ac-node__inner ao-pressable" to={`/academy/course/lesson/${lesson.id}`}>
          {body}
        </Link>
      )}
    </li>
  );
}

export function UnitCard({ unit, next, index, exam }: { unit: PathUnit; next: string | null; index: number; exam: string }) {
  const { i18n } = useTranslation();
  const { ct, contentLang } = useCourseText(exam);
  const done = unit.lessons.filter((l) => l.state === "completed").length;
  const title = titles(unit.title, contentLang, i18n.language);
  return (
    <GlassCard as="section" className="ac-card ac-unit ao-enter" style={{ "--ao-i": index } as CSSProperties} aria-labelledby={`unit-${unit.id}`}>
      <div className="ac-unit__head">
        <Ring ratio={unit.lessons.length ? done / unit.lessons.length : 0} size={48} stroke={5} tone={done === unit.lessons.length ? "success" : "accent"}>
          <span className="ac-unit__ratio ao-num">
            {done}/{unit.lessons.length}
          </span>
        </Ring>
        <div className="ac-unit__text">
          <p className="ac-eyebrow ao-num">{ct("course.unit", { code: unit.code })}</p>
          <h3 id={`unit-${unit.id}`} className="ac-title-3" lang={contentLang}>
            {title.main}
          </h3>
          {title.sub && <p className="ac-footnote">{title.sub}</p>}
        </div>
      </div>
      {unit.skills.length > 0 && (
        <ul className="ac-chips ac-unit__skills" aria-label={ct("course.skills")}>
          {unit.skills.map((skill) => (
            <li key={skill} className="ac-chip">
              {ct(`course.skill.${skill}`, { defaultValue: skill })}
            </li>
          ))}
        </ul>
      )}
      <ol className="ac-trail">
        {unit.lessons.map((lesson, i) => (
          <LessonNode key={lesson.id} lesson={lesson} next={lesson.id === next} index={i} exam={exam} />
        ))}
      </ol>
    </GlassCard>
  );
}

function LevelSection({ level, path, label, exam }: { level: PathLevel; path: CoursePath; label: string; exam: string }) {
  const { ct } = useCourseText(exam);
  const lessons = level.units.flatMap((u) => u.lessons);
  const done = lessons.filter((l) => l.state === "completed").length;
  const open = lessons.some((l) => l.id === path.nextLesson) || (!path.nextLesson && level.level === path.startLevel);
  const allLocked = lessons.every((l) => l.state === "locked");
  const complete = done === lessons.length && lessons.length > 0;
  const target = levelTarget(exam, level.bandTarget);
  return (
    <details className={`ac-level${complete ? " ac-level--done" : ""}${allLocked ? " ac-level--locked" : ""}`} open={open}>
      <summary className="ac-level__summary">
        <span className={`ac-pastille ac-tone--${complete ? "success" : allLocked ? "info" : "accent"}`} aria-hidden="true">
          {complete ? <Check size={16} /> : allLocked ? <Lock size={16} /> : <Flag size={16} />}
        </span>
        <span className="ac-level__text">
          <span className="ac-level__name">{label}</span>
          <span className="ac-level__meta">
            {level.cefr && <Badge>{ct("course.cefr", { level: level.cefr })}</Badge>}
            {target && <span className="ac-caption ao-num">{ct("course.bandTarget", { band: target.value })}</span>}
            {level.level === path.startLevel && <Badge tone="accent">{ct("course.startHere")}</Badge>}
          </span>
        </span>
        <span className="ac-level__progress">
          <span className="ac-caption ao-num">{ct("course.lessonsDone", { done, total: lessons.length })}</span>
          <span className="ac-progress ac-progress--success" aria-hidden="true">
            <span style={{ transform: `scaleX(${lessons.length ? done / lessons.length : 0})` }} />
          </span>
        </span>
        <ChevronDown className="ac-level__chevron" aria-hidden size={18} />
      </summary>
      <div className="ac-units">
        {level.units.map((unit, index) => (
          <UnitCard key={unit.id} unit={unit} next={path.nextLesson} index={index} exam={exam} />
        ))}
      </div>
    </details>
  );
}

/** « Méthode et astuces »: a visible entry to the method lessons (always open). */
export function MethodEntry({ lessons, free }: { lessons: number; free: boolean }) {
  const { t } = useTranslation();
  return (
    <Link to="/academy/course/method" className="ac-card ac-method-entry ao-pressable">
      <span className="ac-pastille ac-tone--warning" aria-hidden="true">
        <Lightbulb size={18} />
      </span>
      <span className="ac-method-entry__text">
        <span className="ac-card-title">{t("academyCourse.method.title")}</span>
        <span className="ac-secondary">{t("academyCourse.method.entry")}</span>
      </span>
      <span className="ac-method-entry__meta">
        {free && <Badge tone="success">{t("academyCourse.method.free")}</Badge>}
        <span className="ac-caption ao-num">{t("academyCourse.method.lessons", { count: lessons })}</span>
      </span>
    </Link>
  );
}

export function CoursePage() {
  const exam = useCourseExam();
  return <CourseView key={exam} exam={exam} />;
}

function CourseView({ exam }: { exam: string }) {
  const { t, i18n } = useTranslation();
  const { ct, contentLang } = useCourseText(exam);
  const { query, path, method, locked } = useCoursePath(true, exam);
  const format = useIeltsFormat(exam);

  if (query.isLoading) {
    return (
      <div className="ac-page">
        <Skeleton style={{ height: 72, maxWidth: 420 }} />
        <Skeleton style={{ height: 120 }} />
        <Skeleton style={{ height: 320 }} />
      </div>
    );
  }
  if (locked) {
    return (
      <div className="ac-page">
        <PageHead eyebrow={ct("course.eyebrow")} title={ct("course.title")} />
        <MethodEntry lessons={0} free />
        <GlassCard className="ac-card">
          <EmptyState
            icon={<Lock aria-hidden size={22} />}
            title={ct("course.lockedTitle")}
            description={ct("course.lockedText")}
            actions={
              <Link className={buttonClass("primary")} to="/academy/plans">
                {t("academy.plans.choose")}
              </Link>
            }
          />
        </GlassCard>
      </div>
    );
  }
  if (!path) {
    return (
      <GlassCard className="ac-card ac-card--center">
        <p className="ko">{t("common.error")}</p>
      </GlassCard>
    );
  }
  const next = findLesson(path, path.nextLesson);
  const labelOf = (level: PathLevel) => {
    const row = format.levels.find((l) => l.key === level.level);
    return (row ? pick(row.label, i18n.language) : "") || pick(level.title, i18n.language) || level.level;
  };

  return (
    <ContentLangProvider lang={contentLang}>
      <div className="ac-page ac-course">
        <PageHead eyebrow={ct("course.eyebrow")} title={ct("course.title")} subtitle={<p>{ct("course.intro")}</p>} />

        <MotherTonguePrompt />
        {query.data?.status === 200 && query.data.data.trial ? (
          <p className="ac-note" role="note">
            {t("coaching.free.trialCourse")}{" "}
            <Link className="ac-link" to="/academy/plans">
              {t("academy.plans.choose")}
            </Link>
          </p>
        ) : null}

        <div className="ac-course__top">
          <section className="ac-hero-card ac-course__next" aria-labelledby="ac-course-next">
            <div className="ac-hero-card__glow" aria-hidden="true" />
            <p className="ac-hero-card__eyebrow">{next ? ct("course.nextEyebrow", { code: next.lesson.code }) : ct("course.title")}</p>
            <h2 id="ac-course-next" className="ac-hero-card__title" lang={next ? contentLang : undefined}>
              {next ? pick(next.lesson.title, contentLang) : path.total ? ct("course.allDone") : ct("course.empty")}
            </h2>
            {next && <p className="ac-hero-card__text">{`${labelOf(next.level)} · ${pick(next.unit.title, contentLang)}`}</p>}
            {next && (
              <div className="ac-hero-card__actions">
                <Link className={`${buttonClass("secondary", { size: "lg" })} ac-hero-card__cta`} to={`/academy/course/lesson/${next.lesson.id}`}>
                  <Play aria-hidden size={18} />
                  {next.lesson.state === "completed" ? ct("course.again") : ct("course.continue")}
                </Link>
              </div>
            )}
          </section>
          <div className="ac-course__stats">
            <div className="ac-stat">
              <span className="ac-pastille ac-tone--success" aria-hidden="true">
                <Route size={16} />
              </span>
              <div className="ac-stat__body">
                <p className="ac-stat__label">{ct("course.progress")}</p>
                <p className="ac-stat__value ao-num">
                  {path.completed}
                  <span className="ac-stat__unit"> / {path.total}</span>
                </p>
              </div>
            </div>
            <div className="ac-stat">
              <span className="ac-pastille ac-tone--warning" aria-hidden="true">
                <Sparkles size={16} />
              </span>
              <div className="ac-stat__body">
                <p className="ac-stat__label">{t("academy.progress.xp")}</p>
                <p className="ac-stat__value ao-num">{t("academy.progress.xpValue", { xp: path.xp })}</p>
              </div>
            </div>
            <Link to="/academy/course/review" className="ac-stat ac-stat--link ao-pressable">
              <span className="ac-pastille ac-tone--eo" aria-hidden="true">
                <Repeat size={16} />
              </span>
              <span className="ac-stat__body">
                <span className="ac-stat__label">{ct("review.title")}</span>
                <span className="ac-stat__value ao-num">{ct("review.due", { count: path.reviewsDue })}</span>
              </span>
            </Link>
          </div>
        </div>

        {method && method.lessons > 0 && <MethodEntry lessons={method.lessons} free={method.free} />}

        {path.levels.length === 0 ? (
          <GlassCard className="ac-card">
            <EmptyState icon={<Route aria-hidden size={22} />} title={ct("course.empty")} description={ct("course.emptyText")} />
          </GlassCard>
        ) : (
          <div className="ac-levels">
            {path.levels.map((level) => (
              <LevelSection key={level.id} level={level} path={path} label={labelOf(level)} exam={exam} />
            ))}
          </div>
        )}
        <p className="ac-footnote">{ct("estimate")}</p>
      </div>
    </ContentLangProvider>
  );
}
